import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ExternalLink, AlertTriangle, Rss } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Admin management of the RSS news feeds the nightly ingest reads. Feeds shipped
// in the code are listed read-only; an admin can add more, and can disable one
// without a deploy. A submitted URL is fetched and parsed by the backend before
// it is saved, so a dead feed is rejected here rather than failing silently
// every night.
const AdminFeeds = () => {
  const { token } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [exampleUrl, setExampleUrl] = useState('https://variety.com/feed/');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/admin/feeds`, { headers: headers() });
      if (resp.ok) {
        const data = await resp.json();
        setFeeds(data.feeds || []);
        if (data.example_url) setExampleUrl(data.example_url);
      } else {
        setError('Failed to load feeds.');
      }
    } catch {
      setError('Failed to load feeds.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const addFeed = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/admin/feeds`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ name: name.trim(), url: url.trim() }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setMessage(`Added ${data.name} — ${data.article_count} articles found.`);
        setName('');
        setUrl('');
        load();
      } else {
        setError(data.detail || 'Could not add that feed.');
      }
    } catch {
      setError('Could not add that feed.');
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (feed, enabled) => {
    await fetch(`${API_BASE}/admin/feeds/${feed.id}`, {
      method: 'PATCH', headers: headers(), body: JSON.stringify({ enabled }),
    });
    load();
  };

  const removeFeed = async (feed) => {
    await fetch(`${API_BASE}/admin/feeds/${feed.id}`, { method: 'DELETE', headers: headers() });
    load();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-videbits-600 hover:text-videbits-800 mb-4">
          <ArrowLeft className="w-4 h-4" /> Admin
        </Link>

        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Rss className="w-6 h-6 text-videbits-600" /> News feeds
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Feeds the nightly ingest reads. Each article costs one extraction call, so
          add sources that report announcements, casting and release dates.
        </p>

        <form onSubmit={addFeed} className="bg-white border border-gray-200 rounded-xl p-4 mb-8">
          <div className="grid gap-3 sm:grid-cols-[1fr_2fr_auto] sm:items-start">
            <div>
              <label htmlFor="feed-name" className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input
                id="feed-name" value={name} onChange={(e) => setName(e.target.value)}
                required maxLength={120} placeholder="Variety"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="feed-url" className="block text-xs font-medium text-gray-600 mb-1">Feed URL</label>
              <input
                id="feed-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)}
                required placeholder={exampleUrl}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">
                The feed itself, not the site's home page. For example:{' '}
                <button
                  type="button" onClick={() => setUrl(exampleUrl)}
                  className="font-mono text-videbits-600 hover:text-videbits-800 underline"
                >
                  {exampleUrl}
                </button>
              </p>
            </div>
            <button
              type="submit" disabled={saving}
              className="sm:mt-5 inline-flex items-center justify-center gap-1 px-4 py-2 text-sm font-medium rounded-lg bg-videbits-600 text-white hover:bg-videbits-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> {saving ? 'Checking…' : 'Add feed'}
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm text-red-600 flex items-start gap-1.5">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
            </p>
          )}
          {message && <p className="mt-3 text-sm text-green-700">{message}</p>}
        </form>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : (
          <div className="space-y-2">
            {feeds.map((feed) => (
              <div
                key={feed.id || feed.url}
                className={`flex items-center gap-3 bg-white rounded-xl border px-4 py-3 ${
                  feed.enabled ? 'border-gray-200' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {feed.name}
                    {feed.source === 'built-in' && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                        Built in
                      </span>
                    )}
                    {feed.last_error && (
                      <span
                        title={feed.last_error}
                        className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700"
                      >
                        Last run: {feed.last_error}
                      </span>
                    )}
                  </div>
                  <a
                    href={feed.url} target="_blank" rel="noreferrer"
                    className="text-xs text-gray-500 hover:text-videbits-600 truncate inline-flex items-center gap-1"
                  >
                    {feed.url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                </div>
                {feed.source === 'admin' ? (
                  <>
                    <button
                      type="button" onClick={() => setEnabled(feed, !feed.enabled)}
                      className="px-3 py-1.5 text-sm font-medium rounded-lg bg-videbits-50 text-videbits-700 hover:bg-videbits-100"
                    >
                      {feed.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button" onClick={() => removeFeed(feed)} aria-label={`Remove ${feed.name}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-gray-400">Edit in code</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeeds;
